import { FormControl, FormGroup, Grid, Tooltip } from '@mui/material';
import {
  getLabelWithInCompleteIcon,
  getMultiNumericCode,
  isScope,
  isVariableVisible,
} from '../services';
import { InfoDialogIcon, VariableInput } from '.';
import { gridLayout } from './SectionLayout';
import { ESectionInfoType } from '../data/Constants';
import { AppState } from '../store';
import { useSelector } from 'react-redux';
import type {
  IConfigurationVariable,
  IFeatureOrFamily,
  ISection,
  ISectionProps,
} from '../../types';

/**
 * Returns the grid header for a section if not all variables are hidden or suppressed.
 * @param {ISection} section - The section object
 * @returns {React.ReactNode | null} The grid header or null
 */
const getGridData = (section: ISection) => {
  // to render as a control instead of tab
  if (section.type === ESectionInfoType.MultiNumeric) {
    return null;
  }
  const isAllItemHdeOrSuppressed = section.variables.filter((variable: IConfigurationVariable) => {
    return !isVariableVisible(variable);
  });
  if (isAllItemHdeOrSuppressed.length === section.variables.length) {
    return null;
  }
  return (
    <Grid container className={'height-fit-content'} size={gridLayout.fullWidth}>
      <h6 className="title">
        {section.id === ESectionInfoType.Guarded ? 'Guarded Variables' : section.name}
      </h6>
    </Grid>
  );
};

/**
 * Renders a multi-numeric section and its features.
 * @param {ISectionProps} props - Input props
 * @returns {React.JSX.Element | null} The rendered multi-numeric section or null
 */
const MultiNumericSection = (props: ISectionProps): React.JSX.Element | null => {
  const { onChange, section, onOptionalChange } = props;

  //selector
  const showCode = useSelector((state: AppState) => state.userSettings.showCode);

  //get the feature name with code based on show code flag
  const label = showCode ? `${section.name} (${getMultiNumericCode(section.id)})` : section.name;

  if (section.isItemsAvailable) {
    return null;
  }

  return (
    <fieldset
      key={section.id}
      className={
        section.isSearchValueMatched
          ? 'inputFieldset multiNumericFieldSetSearchValueMatched'
          : 'inputFieldset'
      }
    >
      <legend className="inputLegend">
        <Tooltip
          title={label}
          placement="right-start"
          slotProps={{
            popper: {
              className: 'dynamic-input-tooltip',
            },
          }}
        >
          {getLabelWithInCompleteIcon(label ?? '', section.isComplete ?? true)}
        </Tooltip>
        <InfoDialogIcon variable={section as IFeatureOrFamily} type="FF" />
      </legend>

      <FormControl fullWidth variant="outlined" disabled={false}>
        <FormGroup className="w-100">
          <Grid container size={gridLayout.fullWidth}>
            {section.variables?.map((v: IConfigurationVariable) => {
              return (
                <VariableInput
                  key={v.id}
                  onChange={onChange}
                  variable={v}
                  onOptionalChange={onOptionalChange}
                  type={section.type}
                />
              );
            })}
          </Grid>
        </FormGroup>
      </FormControl>
    </fieldset>
  );
};

/**
 * Handles subsections and variables.
 * Renders first variables of the current section, then subsections.
 * @param {ISectionProps} props - The properties for the section component
 * @returns {JSX.Element} The section component
 */
export const Section = ({ onChange, section, onOptionalChange }: ISectionProps) => {
  return (
    <Grid container className={'sectionStyle height-fit-content'} size={gridLayout.fullWidth}>
      {getGridData(section)}
      {section.variables?.map((v) => (
        <Grid
          container
          key={`variable-${v.id}`}
          className={'height-fit-content'}
          size={gridLayout.fullWidth}
        >
          <VariableInput
            onChange={onChange}
            variable={v}
            onOptionalChange={onOptionalChange}
            type=""
          />
        </Grid>
      ))}

      {section.sections
        ?.filter((sec) => !isScope(sec))
        .map((s) => {
          if (s.type === ESectionInfoType.MultiNumeric) {
            return (
              <MultiNumericSection
                key={s.id}
                section={s}
                onChange={onChange}
                onOptionalChange={onOptionalChange}
              />
            );
          }
          return (
            <Section
              key={`section-${s.id}`}
              onChange={onChange}
              section={s}
              onOptionalChange={onOptionalChange}
            />
          );
        })}
    </Grid>
  );
};
